import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  useFetchJefaturaTramos,
  useCreateJefaturaTramo,
  useUpdateJefaturaTramo,
  useDeleteJefaturaTramo,
  useFetchJefaturaConfig,
  useUpdateJefaturaConfig,
} from '@/shared/actions/erp/comisiones/comisiones.actions';
import type { JefaturaTramo, JefaturaConfig } from '@/lib/commissions/types';
import {
  JEFATURA_TRAMOS_PREDEFINIDOS,
  JEFATURA_CONFIG_PREDEFINIDO,
} from '@/lib/commissions/constants';
import LoadingDialog from './LoadingDialog';

interface JefaturaFormProps {
  onSaved: () => void;
}

export default function JefaturaForm({ onSaved }: JefaturaFormProps) {
  const { data: tramos, isLoading } = useFetchJefaturaTramos();
  const { data: config } = useFetchJefaturaConfig();

  const createTramoMutation = useCreateJefaturaTramo({ enableSuccessAlert: true });
  const updateTramoMutation = useUpdateJefaturaTramo({ enableSuccessAlert: true });
  const deleteTramoMutation = useDeleteJefaturaTramo();
  const updateConfigMutation = useUpdateJefaturaConfig({ enableSuccessAlert: true });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTramo, setEditingTramo] = useState<Partial<JefaturaTramo> | null>(null);
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Estados locales para configuración
  const [localConfig, setLocalConfig] = useState<JefaturaConfig>({
    ...(config?.id ? { id: config.id } : {}),
    coordinador_general: (config || JEFATURA_CONFIG_PREDEFINIDO).coordinador_general,
    valor_base_jefatura: null, // Se calcula dinámicamente
    meta_masivos: (config || JEFATURA_CONFIG_PREDEFINIDO).meta_masivos,
    activo: (config || JEFATURA_CONFIG_PREDEFINIDO).activo,
  });

  const handleOpenDialog = (tramo?: JefaturaTramo) => {
    if (tramo) {
      setEditingTramo(tramo);
    } else {
      setEditingTramo({
        desde: 1500,
        hasta: 2500,
        porcentaje: 0.025,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTramo(null);
  };

  const handleSaveTramo = async () => {
    if (!editingTramo) return;

    try {
      if (editingTramo.id) {
        await updateTramoMutation.mutateAsync(editingTramo as JefaturaTramo);
      } else {
        await createTramoMutation.mutateAsync(editingTramo);
      }
      onSaved();
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar tramo:', error);
    }
  };

  const handleDeleteTramo = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este tramo?')) {
      await deleteTramoMutation.mutateAsync(id);
      onSaved();
    }
  };

  const handleRestablecer = async () => {
    if (!confirm('¿Estás seguro de restablecer todos los parámetros a los valores predefinidos?')) {
      return;
    }

    try {
      setLoadingDialogOpen(true);
      setLoadingProgress(0);

      const totalSteps = (tramos?.length || 0) + JEFATURA_TRAMOS_PREDEFINIDOS.length + 1;
      let currentStep = 0;

      // Eliminar todos los tramos existentes
      if (tramos && tramos.length > 0) {
        for (const tramo of tramos) {
          if (tramo.id) {
            await deleteTramoMutation.mutateAsync(tramo.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      // Crear los tramos predefinidos
      for (const tramo of JEFATURA_TRAMOS_PREDEFINIDOS) {
        await createTramoMutation.mutateAsync(tramo);
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      // Actualizar config
      await updateConfigMutation.mutateAsync(JEFATURA_CONFIG_PREDEFINIDO);
      currentStep++;
      setLoadingProgress(Math.round((currentStep / totalSteps) * 100));

      onSaved();
      setLoadingDialogOpen(false);
      setLoadingProgress(0);

      // Actualizar estado local
      setLocalConfig(JEFATURA_CONFIG_PREDEFINIDO);
    } catch (error) {
      console.error('Error al restablecer:', error);
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await updateConfigMutation.mutateAsync(localConfig);
      onSaved();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Parámetros de Jefatura
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleRestablecer}
            size="small"
            color="warning"
          >
            Restablecer
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
          >
            Agregar Tramo
          </Button>
        </Box>
      </Box>

      {/* Configuración General */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Configuración General
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coordinador General"
                value={localConfig.coordinador_general}
                onChange={(e) => setLocalConfig({ ...localConfig, coordinador_general: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Meta Masivos"
                type="number"
                value={localConfig.meta_masivos || 14}
                onChange={(e) => setLocalConfig({ ...localConfig, meta_masivos: e.target.value ? Number(e.target.value) : 14 })}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                              Meta de Contratos por Vendedor Masivo:
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              Número de contratos que debe cumplir cada vendedor de canales masivos para no aplicar ajuste en el cálculo.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="left"
                      >
                        <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'info.light',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'info.main',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  ℹ️ Canales Excluidos del Cálculo:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  El cálculo de jefatura se aplica sobre{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>todos los canales</Box>, excepto los siguientes:{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>Vendedor Call Center</Box>,{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>Vendedor Corporativo GB</Box>,{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>Vendedor Corporativo GV</Box>,{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>Vendedor Corporativo TP</Box> y{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>Coordinador Marketing</Box>.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<FormControlLabel
  control={
    <Switch
      checked={localConfig.activo}
      onChange={(e) => {
        console.log("Nuevo estado:", e.target.checked ? "ACTIVADO" : "DESACTIVADO");
        setLocalConfig({ ...localConfig, activo: e.target.checked });
      }}
    />
  }
  label="Activo"
/>
                <Button
                  variant="contained"
                  onClick={handleSaveConfig}
                  disabled={updateConfigMutation.isPending}
                  size="small"
                >
                  Guardar Configuración
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Tabla de Tramos */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Tramos de Comisión
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Desde</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Hasta</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Porcentaje</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tramos && tramos.length > 0 ? (
              tramos.map((tramo: JefaturaTramo) => (
                <TableRow key={tramo.id}>
                  <TableCell>{tramo.desde}</TableCell>
                  <TableCell>{tramo.hasta}</TableCell>
                  <TableCell>{(Number(tramo.porcentaje) * 100).toFixed(2)}%</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(tramo)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => tramo.id && handleDeleteTramo(tramo.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No hay tramos configurados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para editar/crear tramo */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTramo?.id ? 'Editar Tramo' : 'Nuevo Tramo'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Desde (contratos)"
              type="number"
              fullWidth
              value={editingTramo?.desde || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, desde: Number(e.target.value) })}
            />
            <TextField
              label="Hasta (contratos)"
              type="number"
              fullWidth
              value={editingTramo?.hasta || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, hasta: Number(e.target.value) })}
            />
            <TextField
              label="Porcentaje (decimal, ej: 0.025 para 2.5%)"
              type="number"
              fullWidth
              inputProps={{ step: '0.001' }}
              value={editingTramo?.porcentaje || ''}
              onChange={(e) => setEditingTramo({ ...editingTramo, porcentaje: Number(e.target.value) })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSaveTramo}
            variant="contained"
            disabled={createTramoMutation.isPending || updateTramoMutation.isPending}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <LoadingDialog
        open={loadingDialogOpen}
        progress={loadingProgress}
        title="Restableciendo parámetros de Jefatura..."
        message="Eliminando tramos actuales y restaurando configuración predefinida."
      />
    </Box>
  );
}
